// Change hex color into RGB
export function hexToRgba(hex, opacity) {
  hex = hex.replace("#", "");
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Parse hex to { r, g, b } channels (0-255)
export function hexChannels(hex) {
  const clean = hex.replace("#", "");
  const bigint = parseInt(clean, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
}

// Relative luminance heuristic — picks white or ink foreground on accent bg
export function readableOn(hex, { lightFg = "#FFFFFF", darkFg = "#0F172A" } = {}) {
  const { r, g, b } = hexChannels(hex);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 140 ? darkFg : lightFg;
}

// Convert hex to HSL { h: 0-360, s: 0-100, l: 0-100 }
export function hexToHsl(hex) {
  const { r: r255, g: g255, b: b255 } = hexChannels(hex);
  const r = r255 / 255;
  const g = g255 / 255;
  const b = b255 / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

// Convert HSL back to hex
export function hslToHex(h, s, l) {
  const sFrac = s / 100;
  const lFrac = l / 100;
  const c = (1 - Math.abs(2 * lFrac - 1)) * sFrac;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp >= 0 && hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else if (hp < 6) [r1, g1, b1] = [c, 0, x];
  const m = lFrac - c / 2;
  const to255 = (v) => Math.round((v + m) * 255);
  const toHex = (v) => v.toString(16).padStart(2, "0");
  return `#${toHex(to255(r1))}${toHex(to255(g1))}${toHex(to255(b1))}`.toUpperCase();
}

/**
 * Ensures the accent is dark enough to carry white text on a filled button.
 * If the brand color's HSL lightness exceeds `maxL`, it gets pulled down to
 * `targetL` while preserving hue and saturation — keeping brand identity
 * while making solid buttons readable with a consistent white foreground.
 */
export function ensureReadableAccent(hex, { maxL = 60, targetL = 50 } = {}) {
  const { h, s, l } = hexToHsl(hex);
  if (l <= maxL) return hex;
  return hslToHex(h, s, targetL);
}
